import React, { useMemo, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Calendar, ShoppingCart, CheckSquare, 
  Zap, LucideIcon, Shield, Users 
} from 'lucide-react';
import { AppAction } from '@/types';

/**
 * 🛰️ QuickActionType
 */
export type QuickActionType = 'task' | 'event' | 'grocery';

interface ActionItem {
  id: QuickActionType;
  label: string;
  sub: string;
  icon: LucideIcon;
  color: string;
  shadow: string;
  supportsPersonal: boolean; // 🛡️ Hybrid Scope Metadata
}

interface QuickActionsProps {
  /**
   * ⚡ onAction
   * Now includes the selected scope to ensure correct Firebase collection targeting.
   */
  onAction: (type: QuickActionType, scope: 'personal' | 'shared') => void;
  currentUserId: string;
  currentHouseholdId: string | null;
  activeScopeToggle: 'personal' | 'shared' | 'both';
  dispatch: Dispatch<AppAction>;
}

/**
 * ⚡ QuickActions Widget
 * Refactored for Hybrid Scoping: Intelligent intake for Personal vs Shared data.
 */
export const QuickActions: React.FC<QuickActionsProps> = ({ 
  onAction, 
  dispatch, 
  currentHouseholdId,
  activeScopeToggle 
}) => {
  const { t } = useTranslation();

  const ACTIONS: ActionItem[] = useMemo(() => [
    {
      id: 'task',
      label: t('dash.quick.task', 'Add Task'),
      sub: t('dash.quick.task_sub', 'To-do or project'),
      icon: CheckSquare,
      color: 'bg-indigo-600',
      shadow: 'shadow-indigo-500/20',
      supportsPersonal: true
    },
    {
      id: 'event',
      label: t('dash.quick.event', 'New Event'),
      sub: t('dash.quick.event_sub', 'Schedule a moment'),
      icon: Calendar,
      color: 'bg-emerald-600',
      shadow: 'shadow-emerald-500/20',
      supportsPersonal: true
    },
    {
      id: 'grocery',
      label: t('dash.quick.grocery', 'Buy Item'),
      sub: t('dash.quick.grocery_sub', 'Add to shared list'),
      icon: ShoppingCart,
      color: 'bg-amber-500',
      shadow: 'shadow-amber-500/20',
      supportsPersonal: false // Groceries are contextually shared by default
    }
  ], [t]);

  return (
    <div className="h-full flex flex-col justify-center gap-3 p-2">
      {/* --- WIDGET HEADER --- */}
      <div className="flex items-center justify-between mb-2 ml-2 pr-2">
        <div className="flex items-center gap-2">
          <Zap className="w-3 h-3 text-indigo-500 animate-pulse" />
          <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">
            {t('dash.quick.pulse', 'Fast Intake')}
          </span>
        </div>
        
        {/* 🛡️ SCOPE INDICATOR: Visual confirmation of destination */}
        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-800">
          {activeScopeToggle === 'personal' || !currentHouseholdId ? (
            <Shield className="w-2.5 h-2.5 text-emerald-500" />
          ) : (
            <Users className="w-2.5 h-2.5 text-indigo-500" />
          )}
          <span className="text-[7px] font-black text-slate-500 uppercase tracking-tighter">
            {currentHouseholdId ? activeScopeToggle : 'Personal Only'}
          </span>
        </div>
      </div>

      {/* --- ACTION GRID --- */}
      <div className="grid grid-cols-1 gap-3">
        {ACTIONS.map((action: ActionItem) => {
          const Icon = action.icon;
          const targetScope = (!currentHouseholdId || !action.supportsPersonal) ? 'shared' : (activeScopeToggle === 'both' ? 'shared' : activeScopeToggle);
          // Actual routing logic: Personal if forced, otherwise follow toggle
          const effectiveScope = (!currentHouseholdId || activeScopeToggle === 'personal') && action.supportsPersonal ? 'personal' : 'shared';

          return (
            <button
              key={action.id}
              onClick={() => onAction(action.id, effectiveScope)}
              className="group relative flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900/40 rounded-[1.8rem] border border-transparent hover:border-slate-200 dark:hover:border-slate-800 transition-all active:scale-[0.97] overflow-hidden"
            >
              {/* Icon Block with Scope Marker */}
              <div className="relative">
                <div className={`p-3 rounded-2xl ${action.color} ${action.shadow} text-white transition-transform group-hover:scale-110 shadow-lg z-10`}>
                  <Icon className="w-5 h-5" />
                </div>
                {currentHouseholdId && action.supportsPersonal && (
                  <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-950 flex items-center justify-center ${effectiveScope === 'personal' ? 'bg-emerald-500' : 'bg-indigo-600'}`}>
                    {effectiveScope === 'personal' ? <Shield className="w-2 h-2 text-white" /> : <Users className="w-2 h-2 text-white" />}
                  </div>
                )}
              </div>

              {/* Label Block */}
              <div className="flex-1 text-left z-10">
                <p className="text-[11px] font-black uppercase tracking-tight text-slate-900 dark:text-white leading-none italic">
                  {action.label}
                </p>
                <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 mt-1.5 uppercase tracking-tighter opacity-70">
                  {action.sub} • <span className={effectiveScope === 'personal' ? 'text-emerald-500' : 'text-indigo-500'}>{effectiveScope}</span>
                </p>
              </div>

              {/* Interaction Decorator */}
              <Plus className="w-4 h-4 text-slate-200 dark:text-slate-700 group-hover:text-indigo-500 transition-colors z-10" />
              
              {/* Interactive Glow Background */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-[0.05] transition-opacity duration-300 ${action.color}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};